import { Box } from '@mui/material';
import { DataGrid, type DataGridApi, type DataGridReadyEvent } from '@procureai/data-grid-ui';
import type { GetRowIdParams, SideBarDef } from 'ag-grid-community';
import { useCallback, useRef } from 'react';

import type { SavingsListQuery } from '@/generated/gql/graphql';

import { useSavingsDataGridConfig } from './SavingsList.dataGrid';

type SavingsListRow = SavingsListQuery['savings'][number];

// Module-scoped so they keep a stable reference across renders and don't churn
// grid options on every parent re-render.
const getSavingRowId = ({ data }: GetRowIdParams<SavingsListRow>) => data.id;
const SAVINGS_SIDE_BAR: SideBarDef = { toolPanels: ['columns'] };

interface SavingsListProps {
  rows: SavingsListRow[];
  loading: boolean;
  emptyStateText: string;
  onEdit?: (id: string) => void;
  onDelete?: (id: string, name: string) => void;
  canModify?: boolean;
}

export const SavingsList = ({ rows, loading, emptyStateText, onEdit, onDelete, canModify }: SavingsListProps) => {
  const { columnDefs, defaultColDef } = useSavingsDataGridConfig({
    onEdit,
    onDelete,
    canModify,
  });

  const gridApiRef = useRef<DataGridApi<SavingsListRow> | null>(null);
  const loadingRef = useRef(loading);
  loadingRef.current = loading;
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const onGridReady = useCallback((event: DataGridReadyEvent<SavingsListRow>) => {
    gridApiRef.current = event.api;
    gridApiRef.current.setGridOption('loading', loadingRef.current);
    gridApiRef.current.setGridOption('rowData', rowsRef.current);
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, width: '100%', gap: 2, p: 2 }}>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          overflow: 'hidden',
          borderRadius: 2,
          '& > div': {
            height: '100%',
            width: '100%',
          },
        }}
      >
        <DataGrid<SavingsListRow>
          getRowId={getSavingRowId}
          rowData={rows}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          loading={loading}
          onGridReady={onGridReady}
          sideBar={SAVINGS_SIDE_BAR}
          animateRows
          rowHeight={48}
          suppressCellFocus
          overlayNoRowsTemplate={`<span class="ag-overlay-no-rows-center">${emptyStateText}</span>`}
        />
      </Box>
    </Box>
  );
};
